import type { InputStyleSheet } from "@qendo/theme";
import RNDateTimePicker, { type DateTimePickerEvent } from "@react-native-community/datetimepicker";
import { type DateArg, format, isValid, roundToNearestMinutes, startOfDay } from "date-fns";
import { Platform } from "expo-modules-core";
import { type Dispatch, type ReactNode, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { Button } from "~/components/Button";
import { InlineDatePicker } from "~/components/form/date-picker/InlineDatePicker";
import { FieldLabel, type FieldLabelProps } from "~/components/form/FieldLabel";

import { Space } from "~/constants/sizing";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import {
    clampDate,
    type DateMode,
    formatDateForHumans,
    formatDateString,
    formatDateTimeForHumans,
    formatDateTimeString,
    formatTimeForHumans,
    formatTimeStartOfSecond,
    type Mode,
    mergeDateTime,
    parseInput,
} from "~/util/dates";

export type DateFormatter = (date: DateArg<Date>, mode: Mode) => string;

export type DateTimeRenderProps = {
    date: Date | null;
    mode: Mode;
    formatter: DateFormatter;
    onPress: () => void;
    disabled?: boolean;
    placeholder?: string;
    clearable?: boolean;
    onDateSelected: ((value: DateArg<Date> | null) => void) | Dispatch<SetStateAction<DateArg<Date> | null>>;
    theme: InputStyleSheet;
} & Partial<FieldLabelProps>;

export type DateTimePickerProps = {
    name?: string;
    readonly mode?: Mode;
    value: DateArg<Date> | null;
    readonly onDateSelected: ((value: DateArg<Date> | null) => void) | Dispatch<SetStateAction<DateArg<Date> | null>>;
    disabled?: boolean;
    hideLabel?: boolean;
    placeholder?: string;
    max?: DateArg<Date> | null;
    min?: DateArg<Date> | null;
    renderValue?: (props: DateTimeRenderProps) => ReactNode;
    readonly formatStr?: string;
    clearable?: boolean;
    readonly displayFormatter?: DateFormatter | null;
    readonly displayDateMode?: DateMode;
} & Partial<FieldLabelProps>;

const pickerMode = (mode: Mode): Mode =>
    Platform.select({
        android: mode === "time" ? "time" : "date",
        default: mode,
    });

export const DateTimePicker = ({
    value,
    onDateSelected,
    placeholder,
    min,
    max,
    error,
    renderValue,
    formatStr,
    displayFormatter,
    displayDateMode = "locale",
    mode = "datetime",
    name = "_none",
    ...labelProps
}: DateTimePickerProps) => {
    const { inputs, dark } = useTheme();
    const errorBag = useErrorBag();
    const [selecting, setSelecting] = useState<Mode>(pickerMode(mode));
    const minDate = useMemo(() => (min ? parseInput(min, selecting) : undefined), [min, selecting]);
    const maxDate = useMemo(() => (max ? parseInput(max, selecting) : undefined), [max, selecting]);
    const hasValue = value === (value ?? "unset");
    const upstream = useMemo(() => (value ? parseInput(value, mode) : null), [value, mode]);
    const defaultDate = useMemo(() => {
        const compare =
            mode === "time" && upstream
                ? mergeDateTime(minDate ?? maxDate ?? upstream, upstream)
                : (upstream ?? new Date());

        return roundToNearestMinutes(clampDate(compare, minDate, maxDate));
    }, [upstream, maxDate, minDate, mode]);
    const [current, setCurrent] = useState<Date>(defaultDate);
    const [show, setShow] = useState(false);
    const [date, setDate] = useState<Date>(startOfDay(defaultDate));

    useEffect(() => {
        setCurrent(defaultDate);
        setDate(startOfDay(defaultDate));
    }, [defaultDate]);

    const open = () => {
        if (!show) {
            setShow(true);
            setSelecting(pickerMode(mode));
        }
    };
    const hide = () => {
        if (show) {
            setShow(false);
            setSelecting(pickerMode(mode));
        }
    };

    const formatDate = useCallback(
        (date: DateArg<Date>, as?: Mode) => {
            if (formatStr) {
                return format(date, formatStr);
            }

            switch (as ?? mode) {
                case "date":
                    return formatDateString(date);
                case "time":
                    return formatTimeStartOfSecond(date);
                default:
                    return formatDateTimeString(date);
            }
        },
        [mode, formatStr],
    );

    const formatDisplay = useCallback(
        (date: DateArg<Date>, mode: Mode) => {
            if (displayFormatter) {
                return displayFormatter(date, mode);
            }
            switch (mode) {
                case "date":
                    return formatDateForHumans(date, displayDateMode);
                case "time":
                    return formatTimeForHumans(date);
                default:
                    return formatDateTimeForHumans(date, displayDateMode);
            }
        },
        [displayFormatter, displayDateMode],
    );

    const onChange = (event: DateTimePickerEvent, selectedDate?: Date | string) => {
        const currentDate = selectedDate ? parseInput(selectedDate, selecting) : null;

        if (currentDate && isValid(currentDate) && event.type !== "dismissed") {
            if (mode === "datetime" && selecting === "date") {
                setDate(clampDate(currentDate, minDate, maxDate));
                setSelecting("time");
            } else {
                const finalDate = clampDate(
                    (mode === "time" && mergeDateTime(minDate ?? maxDate ?? currentDate, currentDate)) ||
                        (mode === selecting && currentDate) ||
                        mergeDateTime(date, currentDate),
                    minDate,
                    maxDate,
                );

                setCurrent(finalDate);
                onDateSelected(formatDate(finalDate));
                hide();
            }
        } else {
            setCurrent(currentDate ?? defaultDate);
            onDateSelected(value);
            hide();
        }
    };

    const render = useCallback(
        (props: DateTimeRenderProps) => {
            if (renderValue) {
                return renderValue(props);
            }

            const {
                disabled,
                onPress,
                date,
                error,
                placeholder,
                formatter,
                mode,
                clearable,
                onDateSelected,
                theme,
                ...labelProps
            } = props;

            return (
                <FieldLabel {...labelProps}>
                    <Pressable disabled={disabled} onPress={onPress} style={styles.container}>
                        {clearable && date && (
                            <View style={styles.right}>
                                <Button
                                    icon="denied"
                                    onPress={() => onDateSelected(null)}
                                    size="sm"
                                    variant="neutral-quiet"
                                />
                            </View>
                        )}
                        <Text
                            style={[
                                theme.input,
                                theme.inputText,
                                !date && theme.placeholder,
                                Boolean(error) && theme.error,
                                disabled && theme.disabled,
                            ]}
                        >
                            {date ? formatter(date, mode) : (placeholder ?? " ")}
                        </Text>
                    </Pressable>
                </FieldLabel>
            );
        },
        [renderValue],
    );

    const date_limits: { minimumDate?: Date; maximumDate?: Date } = useMemo(
        () =>
            Object.fromEntries(
                [
                    ["minimumDate", minDate],
                    ["maximumDate", maxDate],
                ].filter(([_key, date]) => filled(date)),
            ),
        [minDate, maxDate],
    );

    return (
        <>
            {show
                ? Platform.select({
                      android: (
                          <>
                              {render({
                                  date: hasValue ? current : null,
                                  onPress: open,
                                  placeholder,
                                  mode,
                                  formatter: formatDisplay,
                                  error: error ?? errorBag.first(name),
                                  onDateSelected,
                                  theme: inputs,
                                  ...labelProps,
                              })}
                              <RNDateTimePicker
                                  {...date_limits}
                                  value={current}
                                  mode={selecting}
                                  onChange={onChange}
                                  themeVariant={dark ? "dark" : "light"}
                              />
                          </>
                      ),
                      default: (
                          <InlineDatePicker
                              {...labelProps}
                              {...date_limits}
                              value={current}
                              mode={selecting}
                              onChange={onChange}
                          />
                      ),
                  })
                : render({
                      date: hasValue ? current : null,
                      onPress: open,
                      placeholder,
                      mode,
                      formatter: formatDisplay,
                      error: error ?? errorBag.first(name),
                      onDateSelected,
                      theme: inputs,
                      ...labelProps,
                  })}
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        position: "relative",
    },
    right: {
        position: "absolute",
        top: 2,
        bottom: 2,
        right: Space.sm,
        justifyContent: "center",
        alignItems: "flex-end",
        zIndex: 10,
    },
});
